import React, { Component } from 'react';
import MediaQuery from 'react-responsive';

import withMobileDialog from '@material-ui/core/withMobileDialog';
import { default as AntDrawer } from 'components/ui/Drawer';

import HeaderWithPeriod from 'components/shared/HeaderWithPeriod';
import TableFilters from 'components/ui/TableFilters';
import TabbedTable from 'components/ui/TabbedTable';
import MobileTable from 'components/ui/MobileTable';
import {
  StyledHeader,
  StyledMain,
  StyledHeaderContainer,
  StyledAddButton,
  StyledTableContainer,
} from 'helpers/StyledTableComponentsHelper';
import StatusTag from 'components/ui/StatusTag';

import camelCase from 'lodash/camelCase';
import { toFriendlyDateString } from 'helpers/Formatter';
import { featureEnabled } from 'helpers/ToggleBot';
import { getTranslatedString } from 'helpers/i18n';
import CreateProjectDrawerWizard from './CreateProjectDrawerWizard';

const statuses = ['ontrack', 'overdue'];

class MaintenanceProjectsTable extends Component {
  constructor(props) {
    super(props);
    this.state = {
      filters: {
        search: '',
        properties: Array(this.props.properties.length).fill(false),
        statuses: Array(statuses.length).fill(false),
      },
      filtersActive: false,
      currentTab: 'open',
      sortKey: '',
      sortOrder: '',
      offset: 0,
    };
  }

  setRef = (ref) => (this.table = ref);

  handleTabChange = (tabName) => {
    this.setState(
      {
        currentTab: tabName,
        offset: 0,
      },
      () => this.fetchFilteredProjects()
    );
    if (this.table) this.table.scrollToTop(0);
  };

  handleSort = (key, order) => {
    this.setState(
      {
        sortKey: key,
        sortOrder: order,
        offset: 0,
      },
      () => this.fetchFilteredProjects()
    );
    if (this.table) this.table.scrollToTop(0);
  };

  handleClearFilters = (setFieldValue) => {
    this.setState(
      {
        filters: {
          search: '',
          properties: Array(this.props.properties.length).fill(false),
          statuses: Array(statuses.length).fill(false),
        },
        filtersActive: false,
        currentTab: 'open',
        sortKey: '',
        sortOrder: '',
        offset: 0,
      },
      () => this.props.fetchAllProjects()
    );

    if (this.table) this.table.scrollToTop(0);
    setFieldValue('search', '');
    setFieldValue('properties', Array(this.props.properties.length).fill(false));
    setFieldValue('statuses', Array(statuses.length).fill(false));
  };

  handleSubmitFilters = (values) => {
    const isFiltersUntouched =
      values.search === '' && !values.properties.includes(true) && !values.statuses.includes(true);
    const newState = {
      filtersActive: !isFiltersUntouched,
      filters: {
        search: values.search,
        properties: values.properties,
        statuses: values.statuses,
      },
      drawerVisible: false,
      offset: 0,
    };

    this.setState(newState, () => this.fetchFilteredProjects());
    if (this.table) this.table.scrollToTop(0);
  };

  fetchFilteredProjects = async () => {
    const { filters, filtersActive, currentTab, offset, sortKey, sortOrder } = this.state;

    let fetchPromise = null;

    let filterOptions = {
      sort_key: sortKey,
      sort_order: sortOrder,
    };

    const filterArray = (array, booleanArrayMask) => {
      return array.filter((_, i) => booleanArrayMask[i]);
    };

    if (filtersActive) {
      const propertiesToFilter = filterArray(this.props.properties, filters.properties).map(
        (data) => data.value
      );
      const statusesToFilter = filterArray(statuses, filters.statuses);

      filterOptions = {
        query: filters.search,
        property_ids: propertiesToFilter,
        statuses: statusesToFilter,
        sort_key: sortKey,
        sort_order: sortOrder,
      };

      fetchPromise = this.props.fetchFilteredProjects('all', offset, filterOptions);
    } else {
      fetchPromise = this.props.fetchFilteredProjects(currentTab, offset, filterOptions);
    }

    this.props.fetchProjectStats();
    await fetchPromise;
    const self = this;
    this.setState({ offset: self.props.data.length });
  };

  columns = [
    {
      title: getTranslatedString('landlordPortal.maintenance.projects.status'),
      dataKey: 'status',
      sortable: true,
      render: ({ data }) => <StatusTag status={data} />,
      width: 75,
    },
    {
      title: getTranslatedString('landlordPortal.maintenance.projects.projectName'),
      dataKey: 'name',
      sortable: true,
      width: 300,
    },
    {
      title: getTranslatedString('landlordPortal.maintenance.projects.openTickets'),
      dataKey: 'open_tickets',
      sortable: true,
      width: 150,
      render: ({ data }) => data || '-',
    },
    {
      title: getTranslatedString('landlordPortal.maintenance.projects.closedTickets'),
      dataKey: 'closed_tickets',
      sortable: true,
      width: 150,
      render: ({ data }) => data || '-',
    },
    {
      title: getTranslatedString('landlordPortal.maintenance.projects.dueDate'),
      dataKey: 'due_at',
      sortable: true,
      width: 150,
      render: ({ data }) => toFriendlyDateString(data) || '-',
    },
    {
      title: getTranslatedString('landlordPortal.maintenance.projects.dateCreated'),
      dataKey: 'created_at',
      sortable: true,
      render: ({ data }) => {
        return data ? toFriendlyDateString(data) : '-';
      },
      width: 150,
    },
  ];

  toggleDialog = () => {
    this.setState({
      createProjectDrawerOpen: !this.state.createProjectDrawerOpen,
    });
  };

  onSuccessChange = () => {
    this.setState({ offset: 0 }, () => this.fetchFilteredProjects());
    if (this.table) this.table.scrollToTop(0);
  };

  render() {
    const { filters } = this.state;
    const { data } = this.props;

    const filterOptions = [
      {
        useSearch: true,
        options: this.props.properties.map((data) => data.label),
        name: 'properties',
        title: getTranslatedString('landlordPortal.rentRoll.selectPropertiesToFilter'),
      },
      {
        useSearch: false,
        options: statuses.map((status) => {
          return getTranslatedString(`landlordPortal.statusTag.apiStatus.${camelCase(status)}`);
        }),
        name: 'statuses',
        title: getTranslatedString('landlordPortal.maintenance.projects.filterByStatus'),
      },
    ];

    let numOfOpenTemplates = 0;

    if (this.props.projectCounts) {
      numOfOpenTemplates = this.props.projectCounts.open || 0;
    }

    const tabs = [
      {
        title: getTranslatedString('landlordPortal.maintenance.projects.open'),
        label: getTranslatedString('landlordPortal.maintenance.projects.open'),
        value: 'open',
        count: numOfOpenTemplates,
        onClick: () => this.handleTabChange('open'),
      },
    ];
    return (
      <StyledMain>
        <StyledHeaderContainer>
          <StyledHeader>
            <HeaderWithPeriod
              headerText={getTranslatedString(
                'landlordPortal.maintenance.projects.maintenanceProjects'
              )}
              newStyle
            />
            <StyledAddButton accentColor={this.accentColor} onClick={this.toggleDialog}>
              {getTranslatedString('landlordPortal.maintenance.projects.addNew')}
            </StyledAddButton>
          </StyledHeader>
          <TableFilters
            filters={filters}
            handleSubmitFilters={this.handleSubmitFilters}
            handleClearFilters={this.handleClearFilters}
            filterOptions={filterOptions}
          />
        </StyledHeaderContainer>
        <StyledTableContainer>
          {this._renderDesktopView({ data, tabs })}
          {this._renderMobileView({
            data,
            tabs,
            currentTab: this.state.currentTab,
            tabChange: this.handleTabChange,
          })}
        </StyledTableContainer>
        {this._renderCreateDialog()}
      </StyledMain>
    );
  }

  _renderCreateDialog = () => (
    <AntDrawer
      maskClosable={false}
      keyboard
      visible={this.state.createProjectDrawerOpen}
      onClose={this.toggleDialog}
      closable={false}
      width={600}
    >
      {this.state.createProjectDrawerOpen && (
        <CreateProjectDrawerWizard
          handleClose={this.toggleDialog}
          onSuccess={this.onSuccessChange}
          properties={this.props.properties}
          units={this.props.units}
          postProject={this.props.postProject}
          fetchUnitsOfProperty={this.props.fetchUnitsOfProperty}
        />
      )}
    </AntDrawer>
  );

  _renderDesktopView = ({ data, tabs }) => {
    let groupBy = 'property_name';
    if (featureEnabled('ui.properties.namespace_by_city')) {
      groupBy = 'property_name_city';
    }
    return (
      <MediaQuery query="(min-device-width: 750px)">
        <TabbedTable
          setRef={this.setRef}
          groupRowsBy={groupBy}
          tabs={tabs}
          columns={this.columns}
          data={data}
          searchTotalResultsCount={this.props.searchTotalResultsCount}
          loadMoreData={this.fetchFilteredProjects}
          isLoading={this.props.isLoading}
          filtersActive={this.state.filtersActive}
          onRowClick={this.props.onRowClick}
          onColumnClick={(data) => this.handleSort(data.key, data.order)}
        />
      </MediaQuery>
    );
  };

  _renderMobileView = ({ data, tabs, currentTab, tabChange }) => {
    let groupBy = 'property_name';
    if (featureEnabled('ui.properties.namespace_by_city')) {
      groupBy = 'property_name_city';
    }
    return (
      <MediaQuery query="(max-device-width: 750px)">
        <MobileTable
          groupRowsBy={groupBy}
          data={data}
          tabs={tabs}
          tabIndex={currentTab}
          tabChange={tabChange}
          filtersActive={this.state.filtersActive}
          columns={this.columns}
          tag="status"
          title="name"
          stickyHeaderTop={this.state.filters.search ? '137px' : '107px'}
          fieldsAboveFold={[
            {
              title: getTranslatedString('landlordPortal.maintenance.projects.dueDate'),
              key: 'due_at',
            },
            {
              title: getTranslatedString('landlordPortal.maintenance.projects.dateCreated'),
              key: 'created_at',
            },
          ]}
          fieldsBelowFold={[
            {
              title: getTranslatedString('landlordPortal.maintenance.projects.openTickets'),
              key: 'open_tickets',
            },
            {
              title: getTranslatedString('landlordPortal.maintenance.projects.closedTickets'),
              key: 'closed_tickets',
            },
          ]}
          onRowClick={this.props.onRowClick}
        />
      </MediaQuery>
    );
  };
}

export default withMobileDialog()(MaintenanceProjectsTable);
